import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  X, 
  CheckCircle, 
  FileText, 
  AlertCircle, 
  User, 
  Mail, 
  BookOpen, 
  GraduationCap, 
  Layers, 
  ArrowRight, 
  Sparkles, 
  Zap,
  FileCheck,
  FolderOpen,
  Calendar,
  Link2
} from 'lucide-react';

function PublicSubmit() {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  const [programs, setPrograms] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  const [availableDegrees, setAvailableDegrees] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    type: 'Cours',
    programId: '',
    degreeId: '',
    levelId: '',
    subjectId: '',
    file: null,
    correctionFile: null,  // NEW: correction file
    hasCorrection: false,   // NEW: checkbox for correction
    authorName: '',
    authorEmail: ''
  });

  const resourceTypes = [
    { id: 'Cours', name: 'Cours', icon: <BookOpen size={16} />, color: 'mint', description: 'Documents de cours, supports pédagogiques' },
    { id: 'Examens', name: 'Examens', icon: <FileText size={16} />, color: 'pink', description: 'Sujets d\'examens, corrections' },
    { id: 'TP', name: 'TP', icon: <Zap size={16} />, color: 'yellow', description: 'Travaux pratiques, exercices' },
    { id: 'TD', name: 'TD', icon: <FileCheck size={16} />, color: 'orange', description: 'Travaux dirigés, séries' },
    { id: 'Projets', name: 'Projets', icon: <FolderOpen size={16} />, color: 'purple', description: 'Projets, rapports, présentations' }
  ];

  // Types that can have corrections
  const typesThatCanHaveCorrection = ['Examens', 'TD', 'TP'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programsRes, degreesRes, levelsRes, subjectsRes] = await Promise.all([
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/programs'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/degrees?populate=program'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels?populate=degree'),
          fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects?populate=level')
        ]);
        
        const programsData = await programsRes.json();
        const degreesData = await degreesRes.json();
        const levelsData = await levelsRes.json();
        const subjectsData = await subjectsRes.json();
        
        setPrograms(programsData.data || []);
        setDegrees(degreesData.data || []);
        setLevels(levelsData.data || []);
        setSubjects(subjectsData.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.programId && formData.programId !== '') {
      const programIdNum = parseInt(formData.programId);
      const filtered = degrees.filter(degree => {
        const degreeProgramId = degree.program?.id || degree.program?.data?.id;
        return degreeProgramId === programIdNum;
      });
      setAvailableDegrees(filtered);
    } else {
      setAvailableDegrees([]);
    }
    setFormData(prev => ({ ...prev, degreeId: '', levelId: '', subjectId: '' }));
  }, [formData.programId, degrees]);

  useEffect(() => {
    if (formData.degreeId && formData.degreeId !== '') {
      const degreeIdNum = parseInt(formData.degreeId);
      const filtered = levels.filter(level => {
        const levelDegreeId = level.degree?.id || level.degree?.data?.id;
        return levelDegreeId === degreeIdNum;
      });
      setAvailableLevels(filtered);
    } else {
      setAvailableLevels([]);
    }
    setFormData(prev => ({ ...prev, levelId: '', subjectId: '' }));
  }, [formData.degreeId, levels]);

  useEffect(() => {
    if (formData.levelId && formData.levelId !== '') {
      const levelIdNum = parseInt(formData.levelId);
      const filtered = subjects.filter(subject => {
        const subjectLevelId = subject.level?.id || subject.level?.data?.id;
        return subjectLevelId === levelIdNum;
      });
      setAvailableSubjects(filtered);
    } else {
      setAvailableSubjects([]);
    }
    setFormData(prev => ({ ...prev, subjectId: '' }));
  }, [formData.levelId, subjects]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      if (type === 'main') {
        setFormData({ ...formData, file });
      } else {
        setFormData({ ...formData, correctionFile: file });
      }
      setError('');
    } else {
      setError('Veuillez sélectionner un fichier PDF valide');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      if (type === 'main') {
        setFormData({ ...formData, file });
      } else {
        setFormData({ ...formData, correctionFile: file });
      }
      setError('');
    } else {
      setError('Veuillez sélectionner un fichier PDF valide');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.subjectId || !formData.file) {
      setError('Titre, matière et fichier requis');
      return;
    }
    
    // If type can have correction and user wants to add one, require correction file
    if (formData.hasCorrection && typesThatCanHaveCorrection.includes(formData.type) && !formData.correctionFile) {
      setError('Veuillez sélectionner le fichier de correction');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Step 1: Upload main file
      const uploadFormData = new FormData();
      uploadFormData.append('files', formData.file);
      
      const uploadRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/upload', {
        method: 'POST',
        body: uploadFormData
      });
      
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error('Upload failed');
      
      const fileId = uploadData[0].id;
      
      // Step 2: Create main resource
      const selectedSubject = subjects.find(s => s.id === parseInt(formData.subjectId));
      if (!selectedSubject) throw new Error('Matière non trouvée');
      
      const resourceData = {
        data: {
          title: formData.title,
          type: formData.type,
          approvalStatus: 'pending',
          file: fileId,
          subject: {
            connect: [selectedSubject.documentId]
          }
        }
      };
      
      const createRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resourceData)
      });
      
      const createData = await createRes.json();
      if (!createRes.ok) throw new Error(createData.error?.message || 'Resource creation failed');
      
      const mainResourceId = createData.data.id;
      const mainResourceDocumentId = createData.data.documentId;
      
      // Step 3: If correction file is provided, upload and link it
      let correctionResourceId = null;
      if (formData.hasCorrection && formData.correctionFile && typesThatCanHaveCorrection.includes(formData.type)) {
        // Upload correction file
        const correctionFormData = new FormData();
        correctionFormData.append('files', formData.correctionFile);
        
        const correctionUploadRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/upload', {
          method: 'POST',
          body: correctionFormData
        });
        
        const correctionUploadData = await correctionUploadRes.json();
        if (!correctionUploadRes.ok) throw new Error('Correction upload failed');
        
        const correctionFileId = correctionUploadData[0].id;
        
        // Create correction resource
        const correctionResourceData = {
          data: {
            title: `Correction - ${formData.title}`,
            type: 'Correction',
            approvalStatus: 'pending',
            file: correctionFileId,
            subject: {
              connect: [selectedSubject.documentId]
            }
          }
        };
        
        const correctionCreateRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(correctionResourceData)
        });
        
        const correctionCreateData = await correctionCreateRes.json();
        if (!correctionCreateRes.ok) throw new Error('Correction resource creation failed');
        
        correctionResourceId = correctionCreateData.data.id;
        
        // Link correction to main resource
        const linkRes = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${mainResourceDocumentId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              correction: {
                connect: [correctionCreateData.data.documentId]
              }
            }
          })
        });
        
        if (!linkRes.ok) {
          console.warn('Could not auto-link correction');
        }
      }
      
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
        setFormData({
          title: '',
          type: 'Cours',
          programId: '',
          degreeId: '',
          levelId: '',
          subjectId: '',
          file: null,
          correctionFile: null,
          hasCorrection: false,
          authorName: '',
          authorEmail: ''
        });
      }, 2000);
      
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const getProgramName = (id) => {
    const program = programs.find(p => p.id === parseInt(id));
    return program?.name || '';
  };

  const getDegreeName = (id) => {
    const degree = degrees.find(d => d.id === parseInt(id));
    return degree?.name || '';
  };

  const getLevelName = (id) => {
    const level = levels.find(l => l.id === parseInt(id));
    return level?.name || '';
  };

  const getSubjectName = (id) => {
    const subject = subjects.find(s => s.id === parseInt(id));
    return subject?.name || '';
  };

  const getSubjectSemester = (id) => {
    const subject = subjects.find(s => s.id === parseInt(id));
    return subject?.semester || '';
  };

  const selectedType = resourceTypes.find(t => t.id === formData.type);
  const selectedSemester = formData.subjectId ? getSubjectSemester(formData.subjectId) : '';
  const canHaveCorrection = typesThatCanHaveCorrection.includes(formData.type);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 group bg-gradient-to-r from-mint to-pink text-dark-bg p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
      >
        <Upload size={20} className="group-hover:rotate-12 transition-transform duration-300" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-mono text-sm font-semibold">
          Contribuer
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
          <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            
            <div className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-mint/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink/10 rounded-full blur-3xl"></div>
              <div className="flex justify-between items-center p-6 border-b border-white/10 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mint to-pink flex items-center justify-center">
                    <Upload size={18} className="text-dark-bg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-serif text-white">Contribuer à l'archive</h2>
                    <p className="text-white/40 text-sm mt-0.5">Partagez vos documents avec la communauté ISIMM</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-white/40 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {submitted ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-mint/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={40} className="text-mint" />
                </div>
                <h3 className="text-2xl font-mono text-white mb-2">Merci pour votre contribution !</h3>
                <p className="text-white/40 text-sm">Votre document sera examiné avant publication.</p>
                {formData.hasCorrection && (
                  <p className="text-white/30 text-xs mt-2">La correction a été soumise avec le document principal.</p>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                    <FileText size={14} className="text-mint" />
                    Titre du document *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                    placeholder="Ex: Algorithmique Avancée - Chapitre 4"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                    <Layers size={14} className="text-pink" />
                    Type de document *
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {resourceTypes.map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: type.id, hasCorrection: false }))}
                        className={`p-3 rounded-xl text-center transition-all duration-200 ${
                          formData.type === type.id
                            ? `bg-${type.color} text-dark-bg shadow-md scale-105`
                            : 'bg-dark-card border border-white/10 text-white/60 hover:border-white/30'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          {type.icon}
                          <span className="font-mono text-[10px]">{type.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedType && (
                    <p className="text-white/30 text-xs mt-2">{selectedType.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                    <GraduationCap size={14} className="text-orange" />
                    Programme *
                  </label>
                  <select
                    name="programId"
                    value={formData.programId}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                  >
                    <option value="">Sélectionner un programme</option>
                    {programs.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {formData.programId && (
                  <div className="animate-fade-up">
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <Layers size={14} className="text-yellow" />
                      Département *
                    </label>
                    <select
                      name="degreeId"
                      value={formData.degreeId}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                    >
                      <option value="">Sélectionner un département</option>
                      {availableDegrees.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.degreeId && (
                  <div className="animate-fade-up">
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <Layers size={14} className="text-mint" />
                      Niveau *
                    </label>
                    <select
                      name="levelId"
                      value={formData.levelId}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                    >
                      <option value="">Sélectionner un niveau</option>
                      {availableLevels.map(l => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.levelId && (
                  <div className="animate-fade-up">
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <BookOpen size={14} className="text-pink" />
                      Matière *
                    </label>
                    <select
                      name="subjectId"
                      value={formData.subjectId}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
                    >
                      <option value="">Sélectionner une matière</option>
                      {availableSubjects.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.subjectId && selectedSemester && (
                  <div className="animate-fade-up">
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <Calendar size={14} className="text-mint" />
                      Semestre
                    </label>
                    <div className="w-full px-4 py-3 bg-mint/10 border border-mint/20 rounded-xl text-mint font-mono text-sm">
                      {selectedSemester}
                    </div>
                    <p className="text-white/30 text-xs mt-1">Défini automatiquement par la matière</p>
                  </div>
                )}

                <div>
                  <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                    <Upload size={14} className="text-yellow" />
                    Fichier PDF *
                  </label>
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={(e) => handleDrop(e, 'main')}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                      dragActive 
                        ? 'border-mint bg-mint/10 scale-[0.99]' 
                        : formData.file 
                          ? 'border-mint/50 bg-mint/5' 
                          : 'border-white/20 hover:border-mint/50 bg-dark-card/30'
                    }`}
                    onClick={() => document.getElementById('fileInput').click()}
                  >
                    <input id="fileInput" type="file" accept=".pdf" onChange={(e) => handleFileChange(e, 'main')} className="hidden" />
                    {formData.file ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-mint/20 flex items-center justify-center">
                          <FileText size={24} className="text-mint" />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-mono text-sm">{formData.file.name}</p>
                          <p className="text-white/30 text-xs">{(formData.file.size / 1024).toFixed(2)} KB</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData({ ...formData, file: null });
                          }}
                          className="ml-4 p-1 rounded-lg bg-white/10 hover:bg-white/20 transition"
                        >
                          <X size={14} className="text-white/60" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-mint/20 to-pink/20 flex items-center justify-center">
                          <Upload size={28} className="text-mint" />
                        </div>
                        <p className="text-white/60 text-sm">Glissez votre PDF ici ou <span className="text-mint">cliquez pour parcourir</span></p>
                        <p className="text-white/20 text-xs mt-2">PDF uniquement, max 10MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Correction Option - Only for Examens, TD, TP */}
                {canHaveCorrection && (
                  <div className="animate-fade-up">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="hasCorrection"
                        checked={formData.hasCorrection}
                        onChange={(e) => setFormData({ ...formData, hasCorrection: e.target.checked, correctionFile: null })}
                        className="w-4 h-4 rounded border-white/20 bg-dark-card text-mint focus:ring-mint focus:ring-offset-0"
                      />
                      <label htmlFor="hasCorrection" className="text-white/70 text-sm font-mono flex items-center gap-2 cursor-pointer">
                        <Link2 size={14} className="text-yellow" />
                        Je souhaite également soumettre une correction
                      </label>
                    </div>
                    
                    {formData.hasCorrection && (
                      <div className="ml-6 mt-3">
                        <div
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={(e) => handleDrop(e, 'correction')}
                          className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                            dragActive 
                              ? 'border-yellow bg-yellow/10 scale-[0.99]' 
                              : formData.correctionFile 
                                ? 'border-yellow/50 bg-yellow/5' 
                                : 'border-white/20 hover:border-yellow/50 bg-dark-card/30'
                          }`}
                          onClick={() => document.getElementById('correctionInput').click()}
                        >
                          <input id="correctionInput" type="file" accept=".pdf" onChange={(e) => handleFileChange(e, 'correction')} className="hidden" />
                          {formData.correctionFile ? (
                            <div className="flex items-center justify-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-yellow/20 flex items-center justify-center">
                                <FileText size={24} className="text-yellow" />
                              </div>
                              <div className="text-left">
                                <p className="text-white font-mono text-sm">{formData.correctionFile.name}</p>
                                <p className="text-white/30 text-xs">{(formData.correctionFile.size / 1024).toFixed(2)} KB</p>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData({ ...formData, correctionFile: null });
                                }}
                                className="ml-4 p-1 rounded-lg bg-white/10 hover:bg-white/20 transition"
                              >
                                <X size={14} className="text-white/60" />
                              </button>
                            </div>
                          ) : (
                            <div>
                              <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-yellow/20 flex items-center justify-center">
                                <Upload size={24} className="text-yellow" />
                              </div>
                              <p className="text-white/60 text-sm">Glissez la correction ici ou <span className="text-yellow">cliquez pour parcourir</span></p>
                              <p className="text-white/30 text-xs mt-1">La correction sera automatiquement liée à votre document</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <User size={14} className="text-white/40" />
                      Votre nom
                    </label>
                    <input
                      type="text"
                      name="authorName"
                      value={formData.authorName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint transition-all"
                      placeholder="Optionnel"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm font-mono mb-2 flex items-center gap-2">
                      <Mail size={14} className="text-white/40" />
                      Email
                    </label>
                    <input
                      type="email"
                      name="authorEmail"
                      value={formData.authorEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint transition-all"
                      placeholder="Pour vous contacter"
                    />
                  </div>
                </div>

                {formData.subjectId && (
                  <div className="bg-gradient-to-r from-mint/10 to-pink/10 rounded-xl p-4 border border-mint/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles size={14} className="text-mint" />
                      <p className="text-mint text-xs font-mono">RÉCAPITULATIF</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <GraduationCap size={12} className="text-orange" />
                        <span className="text-white/60">Programme:</span>
                        <span className="text-white font-mono text-xs">{getProgramName(formData.programId)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Layers size={12} className="text-yellow" />
                        <span className="text-white/60">Département:</span>
                        <span className="text-white font-mono text-xs">{getDegreeName(formData.degreeId)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Layers size={12} className="text-mint" />
                        <span className="text-white/60">Niveau:</span>
                        <span className="text-white font-mono text-xs">{getLevelName(formData.levelId)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen size={12} className="text-pink" />
                        <span className="text-white/60">Matière:</span>
                        <span className="text-white font-mono text-xs">{getSubjectName(formData.subjectId)}</span>
                      </div>
                      {selectedSemester && (
                        <div className="flex items-center gap-2">
                          <Calendar size={12} className="text-mint" />
                          <span className="text-white/60">Semestre:</span>
                          <span className="text-white font-mono text-xs">{selectedSemester}</span>
                        </div>
                      )}
                      {formData.hasCorrection && formData.correctionFile && (
                        <div className="flex items-center gap-2 pt-2 border-t border-white/10 mt-2">
                          <Link2 size={12} className="text-yellow" />
                          <span className="text-white/60">Correction:</span>
                          <span className="text-yellow font-mono text-xs">Incluse</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-pink/10 border border-pink/30 rounded-xl p-3 flex items-center gap-2">
                    <AlertCircle size={16} className="text-pink" />
                    <p className="text-pink text-xs">{error}</p>
                  </div>
                )}

                <div className="bg-dark-card/50 rounded-xl p-4 border border-white/5">
                  <div className="flex gap-3">
                    <AlertCircle size={18} className="text-yellow flex-shrink-0" />
                    <p className="text-white/30 text-xs leading-relaxed">
                      Les documents soumis seront vérifiés avant publication. Assurez-vous de posséder les droits de partage.
                      {canHaveCorrection && (
                        <span className="block mt-1 text-white/20">
                          💡 Astuce: Vous pouvez soumettre un document et sa correction en même temps. La correction sera automatiquement liée.
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 hover:text-white transition-all font-mono text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !formData.title || !formData.subjectId || !formData.file}
                    className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg hover:shadow-mint/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-dark-bg border-t-transparent rounded-full animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <ArrowRight size={14} />
                        Soumettre
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fadeUp 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
}

export default PublicSubmit;